import { useCallback, useMemo } from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import { QuestionDTO, ReorderQuestionsDTO } from '../dtos';
import { questionQueryKeys } from './queryKeys';
import { useReorderQuestions, useReorderTemplateQuestions } from './useQuestions';

interface UseQuestionReorderProps {
  surveyId: string;
  questions: QuestionDTO[];
}

export const useQuestionReorder = ({ surveyId, questions }: UseQuestionReorderProps) => {
  const queryClient = useQueryClient();
  const reorderMutation = useReorderQuestions(surveyId);

  // Optimistically update the questions order in cache
  const updateQuestionsOrder = useCallback(
    (reorderedCustomQuestions: QuestionDTO[]) => {
      queryClient.setQueryData(
        questionQueryKeys.questionsBySurvey(surveyId),
        (oldData: unknown) => {
          const typed = oldData as { data?: { questions?: QuestionDTO[] } } | undefined;
          if (!typed?.data?.questions) return oldData;

          // Preserve template questions and only replace custom questions
          const templateQuestions = typed.data.questions.filter((q) => q.isFromTemplate === true);

          return {
            ...typed,
            data: {
              ...typed.data,
              questions: [...templateQuestions, ...reorderedCustomQuestions],
            },
          };
        }
      );
    },
    [queryClient, surveyId]
  );

  // Revert optimistic update on error
  const revertOptimisticUpdate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: questionQueryKeys.questionsBySurvey(surveyId),
    });
  }, [queryClient, surveyId]);

  // Handle drag end event
  const onDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      // If dropped outside of droppable area or in same position
      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = questions.findIndex((q) => q.id === active.id);
      const newIndex = questions.findIndex((q) => q.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      // Create new questions array with updated order using arrayMove
      const reorderedQuestions = arrayMove(questions, oldIndex, newIndex);

      // Update order numbers to match array indices
      const questionsWithNewOrder = reorderedQuestions.map((question, index) => ({
        ...question,
        order: index + 1,
      }));

      // Optimistically update the UI
      updateQuestionsOrder(questionsWithNewOrder);

      try {
        // Prepare reorder request data
        const reorderData: ReorderQuestionsDTO = {
          questionOrders: questionsWithNewOrder.map((question) => ({
            questionId: question.id,
            newOrder: question.order,
          })),
        };

        // Call API to persist the new order
        await reorderMutation.mutateAsync(reorderData);
      } catch (error) {
        // Revert optimistic update on error
        revertOptimisticUpdate();

        // The error will be handled by the mutation's onError callback
        // which shows a notification to the user
      }
    },
    [questions, updateQuestionsOrder, reorderMutation, revertOptimisticUpdate]
  );

  // Check if reordering is currently in progress
  const isReordering = reorderMutation.isPending;

  // Memoize sorted questions to avoid unnecessary re-renders
  const sortedQuestions = useMemo(
    () => [...questions].sort((a, b) => a.order - b.order),
    [questions]
  );

  return {
    onDragEnd,
    isReordering,
    sortedQuestions,
  };
};

// Template-specific question reorder hook
interface UseTemplateQuestionReorderProps {
  tenantId: string;
  templateId: string;
  questions: QuestionDTO[];
}

export const useTemplateQuestionReorder = ({
  tenantId,
  templateId,
  questions,
}: UseTemplateQuestionReorderProps) => {
  const queryClient = useQueryClient();
  const reorderMutation = useReorderTemplateQuestions(tenantId, templateId);

  // Optimistically update the questions order in cache
  const updateQuestionsOrder = useCallback(
    (newQuestions: QuestionDTO[]) => {
      queryClient.setQueryData(
        questionQueryKeys.questionsByTemplate(tenantId, templateId),
        (oldData: unknown) => {
          const typed = oldData as { data?: { questions?: QuestionDTO[] } } | undefined;
          if (!typed?.data) return oldData;

          return {
            ...typed,
            data: {
              ...typed.data,
              questions: newQuestions,
            },
          };
        }
      );
    },
    [queryClient, tenantId, templateId]
  );

  // Revert optimistic update on error
  const revertOptimisticUpdate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: questionQueryKeys.questionsByTemplate(tenantId, templateId),
    });
  }, [queryClient, tenantId, templateId]);

  // Handle drag end event
  const onDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      // If dropped outside of droppable area or in same position
      if (!over || active.id === over.id) {
        return;
      }

      // Find the old and new indices
      const oldIndex = questions.findIndex((q) => q.id === active.id);
      const newIndex = questions.findIndex((q) => q.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      // Reorder questions array
      const reorderedQuestions = arrayMove(questions, oldIndex, newIndex);

      // Update order numbers based on new positions
      const questionsWithNewOrder = reorderedQuestions.map((question, index) => ({
        ...question,
        order: index + 1,
      }));

      // Optimistically update the UI
      updateQuestionsOrder(questionsWithNewOrder);

      try {
        // Prepare reorder request data
        const reorderData: ReorderQuestionsDTO = {
          questionOrders: questionsWithNewOrder.map((question) => ({
            questionId: question.id,
            newOrder: question.order,
          })),
        };

        // Call API to persist the new order
        await reorderMutation.mutateAsync(reorderData);
      } catch (error) {
        // Revert optimistic update on error
        revertOptimisticUpdate();

        // The error will be handled by the mutation's onError callback
        // which shows a notification to the user
      }
    },
    [questions, updateQuestionsOrder, reorderMutation, revertOptimisticUpdate]
  );

  // Check if reordering is currently in progress
  const isReordering = reorderMutation.isPending;

  // Memoize sorted questions to avoid unnecessary re-renders
  const sortedQuestions = useMemo(
    () => [...questions].sort((a, b) => a.order - b.order),
    [questions]
  );

  return {
    onDragEnd,
    isReordering,
    sortedQuestions,
  };
};
